import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  CircularProgress,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import React, { useCallback, useEffect, useMemo, useState } from "react";
import cpdActivity from "../../../../../assets/cpd-activity.png";
import { useLanguages } from "../../../../../context/LanguagesContext";
import { getSectionImages, isTipicoContext } from "../../utils/sectionImages";
import { usePortal } from "../../../../../context/PortalsContext";
import { useToast } from "../../../../../context/ToastContext";
import { useTranslation } from "../../../../../context/TranslationContext";
import { getTranslationsManage } from "../../../../../services/api/apiClient";
import {
  TabId,
  TabSectionKey,
  TranslationManageAttributeResult,
} from "../../../../../services/api/types/portalConfiguration/translations";
import { brand, gray } from "../../../../../theme/themePrimitives";
import { fromCamelCase } from "../../../../../utils/helpers";
import { MAX_LENGTH_SHORT_LABEL } from "../../../../../utils/inputLimits";
import { TranslationTabSectionContent } from "../TranslationTabSectionContent/TranslationTabSectionContent";
import { extractErrorMessage } from "../../../../../utils/errorUtils";

interface TranslationTabSectionProps<T extends string> {
  tabSection: TabId;
  tabs: readonly T[];
}

const FALLBACK_IMAGES = [cpdActivity];

// Same Tipico gating as sectionImages.ts — these two attributes are bespoke
// copy for that one portal and shouldn't clutter the manage UI for anyone else.
const TIPICO_ONLY_ATTRIBUTE_KEYS = [
  "acceptLabelTipico",
  "acceptLabelQuestionsTipico",
];

export const TranslationTabSection = <T extends string>({
  tabSection,
  tabs,
}: TranslationTabSectionProps<T>) => {
  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [localSearch, setLocalSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [activeTab, setActiveTab] = useState<T>(tabs[0]);

  const SEARCH_DEBOUNCE = 400;

  useEffect(() => {
    const handle = setTimeout(() => {
      setDebouncedSearch(localSearch.trim());
    }, SEARCH_DEBOUNCE);
    return () => clearTimeout(handle);
  }, [localSearch]);

  const filteredTabs = useMemo(
    () =>
      tabs.filter((tab) =>
        fromCamelCase(tab).toLowerCase().includes(debouncedSearch.toLowerCase())
      ),
    [tabs, debouncedSearch]
  );
  const { portals } = usePortal();
  const { languages } = useLanguages();
  const { updateTranslationSection } = useTranslation();
  const [attributes, setAttributes] = useState<
    TranslationManageAttributeResult[]
  >([]);

  const isMain = !!portals.selectedPortal?.isMain;

  const sectionImages = useMemo(() => {
    const images = getSectionImages(tabSection, activeTab);
    return images.length > 0 ? images : FALLBACK_IMAGES;
  }, [tabSection, activeTab]);

  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setLocalSearch(event.target.value);
  };

  const handleTabChange = (tab: T) => {
    setActiveTab(tab);
  };

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      if (portals.selectedPortal) {
        const data = await getTranslationsManage(
          portals.selectedPortal.portalUid,
          tabSection,
          activeTab,
        );

        const updatedAttributes: Record<string, string> = {};
        data.attributes.forEach((attribute) => {
          const value = attribute.values[languages.selectedLanguage];
          if (value) {
            updatedAttributes[attribute.attributeKey] = value.effectiveValue;
          }
        });

        updateTranslationSection(
          tabSection,
          activeTab as TabSectionKey,
          updatedAttributes,
        );
        setAttributes(
          isTipicoContext()
            ? data.attributes
            : data.attributes.filter(
                (attribute) =>
                  !TIPICO_ONLY_ATTRIBUTE_KEYS.includes(attribute.attributeKey),
              ),
        );
      }
    } catch (error: any) {
      console.log("Error:", error);
      showToast(
        extractErrorMessage(error, "Failed to fetch portal translations"),
        "error",
      );
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [portals.selectedPortal, tabSection, activeTab, languages.selectedLanguage]);

  useEffect(() => {
    setActiveTab(tabs[0]);
  }, [tabs]);

  useEffect(() => {
    // When filtered tabs change, ensure activeTab is still in the list
    if (filteredTabs.length > 0 && !filteredTabs.includes(activeTab)) {
      setActiveTab(filteredTabs[0]);
    }
  }, [filteredTabs, activeTab]);

  useEffect(() => {
    // Only fetch if activeTab is in filtered tabs (avoids fetch for old tab when search filters change)
    if (
      !activeTab ||
      !tabs.includes(activeTab) ||
      !filteredTabs.includes(activeTab)
    )
      return;
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    portals.selectedPortal,
    activeTab,
    filteredTabs,
    fetchData,
  ]);

  return (
    <>
      {loading ? (
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "30vh",
          }}
        >
          <CircularProgress size={100} />
        </Box>
      ) : (
        <Box mt={2} sx={{ display: "grid", gridTemplateColumns: "1fr 3fr" }}>
          <Box pr={2}>
            <TextField
              size="small"
              placeholder="Search components"
              variant="outlined"
              fullWidth
              value={localSearch}
              onChange={handleSearchChange}
              sx={{
                "& .MuiOutlinedInput-notchedOutline": {
                  border: "none",
                },
                "& .MuiInputBase-root": {
                  bgcolor: "#EAECF0",
                },
              }}
              slotProps={{
                input: {
                  sx: {
                    borderRadius: "12px",
                    backgroundColor: `${gray[100]} !important`,
                    border: "none !important",
                  },
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                },
                htmlInput: { maxLength: MAX_LENGTH_SHORT_LABEL },
              }}
            />
            <Stack spacing={1} mt={2}>
              {filteredTabs.length === 0 ? (
                <Typography variant="body2" color="text.secondary" py={2}>
                  No tabs match your search.
                </Typography>
              ) : (
                filteredTabs.map((tab) => (
                <Box
                  key={tab}
                  onClick={() => handleTabChange(tab)}
                  sx={{
                    backgroundColor: activeTab === tab ? brand[100] : "",
                    borderRadius: "6px",
                    padding: "8px 12px",
                    color: activeTab === tab ? brand[600] : gray[600],
                    cursor: "pointer",
                    "&:hover": {
                      backgroundColor: gray[100],
                    },
                  }}
                >
                  <Typography fontWeight="600">{fromCamelCase(tab)}</Typography>
                </Box>
              ))
              )}
            </Stack>
          </Box>
          <Box pl={2} borderLeft={`1px solid ${gray[200]}`}>
            <TranslationTabSectionContent
              images={sectionImages}
              sectionLabel={fromCamelCase(activeTab)}
              tabId={tabSection}
              tabSectionKey={activeTab as TabSectionKey}
              attributes={attributes}
              isMain={isMain}
              onChanged={fetchData}
            />
          </Box>
        </Box>
      )}
    </>
  );
};
